import { useState, useCallback, useRef, useEffect } from 'react';
import { APIProvider, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

// Internal component that uses the Google Maps context
function AddressSearchInternal({ value, onChange, onPostcodeExtract, placeholder, disabled }) {
  const apiIsLoaded = useApiIsLoaded();
  const [searchQuery, setSearchQuery] = useState(value || '');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const geocoderServiceRef = useRef(null);

  // Initialize services only when API is loaded
  useEffect(() => {
    if (!apiIsLoaded) return;
    if (!window.google?.maps?.places) return;

    console.log('Initializing Google Maps services...');
    autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    geocoderServiceRef.current = new window.google.maps.Geocoder();
    console.log('Services initialized:', {
      autocomplete: !!autocompleteServiceRef.current,
      geocoder: !!geocoderServiceRef.current
    });
  }, [apiIsLoaded]);

  // Sync search query with value prop
  useEffect(() => {
    if (value !== searchQuery) {
      setSearchQuery(value || '');
    }
  }, [value, searchQuery]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    onChange(query); // Update parent immediately with raw input

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(() => {
      if (!autocompleteServiceRef.current) {
        console.warn('Autocomplete service not initialized');
        setIsSearching(false);
        return;
      }

      console.log('Searching for:', query);

      try {
        autocompleteServiceRef.current.getPlacePredictions(
          {
            input: query
          },
          (predictions, status) => {
            console.log('Search result:', { status, predictions });
            setIsSearching(false);

            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSearchResults(predictions);
              setShowResults(true);
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              console.log('No results found');
              setSearchResults([]);
              setShowResults(false);
            } else {
              console.error('Places API error:', status);
              setSearchResults([]);
              setShowResults(false);
            }
          }
        );
      } catch (error) {
        console.error('Error calling getPlacePredictions:', error);
        setIsSearching(false);
      }
    }, 300);
  }, [onChange]);

  const extractPostcode = (addressComponents) => {
    const postcodeComponent = addressComponents?.find(
      component => component.types.includes('postal_code')
    );
    return postcodeComponent?.long_name || '';
  };

  const handleSelectPlace = useCallback((placeId, description) => {
    if (!geocoderServiceRef.current) {
      console.warn('Geocoder service not initialized');
      return;
    }

    console.log('Geocoding place:', placeId);

    // Use Geocoder to get address details including postcode
    try {
      geocoderServiceRef.current.geocode(
        { placeId },
        (results, status) => {
          console.log('Geocode result:', { status, results });

          if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
            const result = results[0];
            const fullAddress = result.formatted_address;

            console.log('Selected address:', fullAddress);
            onChange(fullAddress);
            setSearchQuery(fullAddress);

            // Extract and pass postcode to parent if callback provided
            if (onPostcodeExtract) {
              const postcode = extractPostcode(result.address_components);
              console.log('Extracted postcode:', postcode);
              if (postcode) {
                onPostcodeExtract(postcode);
              }
            }

            setSearchResults([]);
            setShowResults(false);
          } else {
            console.error('Geocoder failed with status:', status);
          }
        }
      );
    } catch (error) {
      console.error('Error calling geocode:', error);
    }
  }, [onChange, onPostcodeExtract]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder={placeholder || "Start typing an address..."}
          className="pl-10"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          disabled={disabled}
        />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Search Results Dropdown */}
      {showResults && searchResults.length > 0 && (
        <Card className="absolute w-full mt-2 z-50 shadow-lg border-2">
          <ScrollArea className="max-h-60">
            <div className="p-2">
              {searchResults.map((result) => (
                <button
                  key={result.place_id}
                  type="button"
                  onClick={() => handleSelectPlace(result.place_id, result.description)}
                  className="w-full text-left p-3 hover:bg-gray-100 rounded-lg transition-colors flex items-start gap-2"
                >
                  <MapPin className="h-4 w-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{result.structured_formatting.main_text}</p>
                    <p className="text-xs text-muted-foreground truncate">{result.structured_formatting.secondary_text}</p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
          <div className="border-t p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => setShowResults(false)}
            >
              Close
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// Main export component with APIProvider wrapper
export function AddressAutocomplete({ value, onChange, onPostcodeExtract, placeholder, disabled, className = '' }) {
  if (!GOOGLE_MAPS_API_KEY) {
    return (
      <Input
        type="text"
        placeholder={placeholder || "Address (Google Maps API not configured)"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={className}
      />
    );
  }

  return (
    <div className={className}>
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
        <AddressSearchInternal
          value={value}
          onChange={onChange}
          onPostcodeExtract={onPostcodeExtract}
          placeholder={placeholder}
          disabled={disabled}
        />
      </APIProvider>
    </div>
  );
}
